import { assertError, extractHTTPError, HTTPStatusCode, type MaybeHTTPError } from '../errors';
import { type DebugLogger, type DebugLoggerFn, Logger } from '../logger';
import { epochTime, time } from '../performance';
import { getHeadersForTrace, getTraceFromHeaders } from './http';
import { generateSpanId, generateTraceId } from './ids';
import type { TraceState } from './types';

/**
 * Span kind used when exporting a trace to OpenTelemetry.
 */
export enum TraceSpanKind {
    Client = 'client',
    Consumer = 'consumer',
    Internal = 'internal',
    Producer = 'producer',
    Server = 'server',
}

/**
 * Value that can be attached to a trace label before OTLP serialization.
 */
export type TraceLabelValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | Date
    | Error
    | ReadonlyArray<TraceLabelValue>
    | { [key: string]: TraceLabelValue };

/**
 * Collection of labels attached to a trace.
 */
export type TraceLabels = Record<string, TraceLabelValue>;

export interface TraceOptions {
    /**
     * If specified, the trace will be considered a root trace.
     * @default false
     */
    rootTrace?: boolean | string;

    /**
     * If specified, the trace will be created as a child of the given HTTP request trace.
     */
    httpHeaders?: {
        [key: string]: any;
    };

    /**
     * Optional sampling rate override consumed by the Sentry tracing layer.
     */
    sampleRate?: number;

    /**
     * Whether this trace should be exported to OTLP.
     * @default true
     */
    otlp?: boolean;

    /**
     * Span kind to export to OpenTelemetry.
     * @default 'internal'
     */
    spanKind?: TraceSpanKind;

    /**
     * If silent, successful completions are logged at debug level.
     */
    silent?: boolean;

    /**
     * Whether successful completion logs should include trace labels.
     * @default true
     */
    logLabelsOnSuccess?: boolean;
}

export interface TraceParentContext {
    traceId: string;
    parentSpanId: string | null;
    traceFlags: number;
}

let globalState: TraceState = {};

/**
 * Interface for a debugging trace.
 * In the backend, traces are tracked on Stackdriver and help debug performances.
 * In the browser, it can be shown by enabling the debug log "trace:"
 */
export class GenericTrace {
    /** ID of the span trace */
    public spanId: string;

    /** ID of the root trace associated */
    public traceId: string;

    /** ID of the parent span trace */
    public parentSpanId: string | null;

    /** Trace flags propagated with this trace, including the sampling decision. */
    public traceFlags: number;

    /** Friendly name for the trace. */
    public name: string;

    /** Debugging labels associated with this trace. */
    public labels: TraceLabels = {};

    /** Attributes exported only to OpenTelemetry spans. */
    public otlpAttributes: TraceLabels = {};

    /** Whether this trace should be exported to OTLP. */
    public otlp: boolean;

    /** OpenTelemetry span kind used when this trace is exported. */
    public spanKind: TraceSpanKind;

    /** State of the trace. It'll be propagated as headers. */
    public traceState: TraceState = {};

    /** Monotonic timestamp when the trace was started. */
    public startTime: number | null;

    /** Monotonic timestamp when the trace was ended. */
    public endTime: number | null;

    /** Wall-clock timestamp when the trace was started, in epoch milliseconds. */
    public startTimestamp: number | null;

    /** Wall-clock timestamp when the trace was ended, in epoch milliseconds. */
    public endTimestamp: number | null;

    /** Logger to logs in the context of this trace. */
    public logger: DebugLogger;

    /** If silent, successful completions are logged at debug level. */
    public silent: boolean;

    /** Whether successful completion logs should include trace labels. */
    public logLabelsOnSuccess: boolean;

    constructor(name: string, opts: TraceOptions = {}) {
        this.name = name;
        this.logger = Logger(`trace:${this.name}`);
        this.startTime = null;
        this.endTime = null;
        this.startTimestamp = null;
        this.endTimestamp = null;
        this.spanId = generateSpanId();
        this.otlp = typeof opts.otlp === 'undefined' ? true : opts.otlp;
        this.spanKind = opts.spanKind ?? TraceSpanKind.Internal;
        this.silent = typeof opts.silent === 'undefined' ? false : opts.silent;
        this.logLabelsOnSuccess = opts.logLabelsOnSuccess ?? true;

        const headers = opts.httpHeaders ? getTraceFromHeaders(opts.httpHeaders) : null;

        if (opts.rootTrace) {
            this.traceId = typeof opts.rootTrace === 'string' ? opts.rootTrace : generateTraceId();
            this.parentSpanId = null;
            this.traceFlags = 0x01;
        } else if (headers) {
            this.traceId = headers.traceId;
            this.parentSpanId = headers.spanId;
            this.traceFlags = headers.traceFlags;
            this.traceState = headers.state;
        } else {
            const parent = this.getParentContext();
            this.parentSpanId = parent.parentSpanId;
            this.traceId = parent.traceId;
            this.traceFlags = parent.traceFlags;
        }
    }

    /**
     * Return the current current root trace.
     */
    public static getCurrentRootTrace(): GenericTrace | null {
        throw new Error('Not implemented');
    }

    /**
     * Return the current running span trace.
     */
    public static getCurrentSpanTrace(): GenericTrace | null {
        throw new Error('Not implemented');
    }

    /**
     * Define global state for all traces.
     */
    public static setGlobalState(state: TraceState) {
        globalState = {
            ...globalState,
            ...state,
        };
    }

    /**
     * Get the context (root trace and parent span) of the current trace.
     */
    public getParentContext(): TraceParentContext {
        throw new Error('Not implemented');
    }

    /**
     * Add a label on this trace.
     */
    public label(name: string, value: TraceLabelValue) {
        this.labels[name] = value;
    }

    /**
     * Define multiple labels on this trace.
     */
    public setLabels(labels: TraceLabels) {
        for (const [key, value] of Object.entries(labels)) {
            this.label(key, value);
        }
    }

    /** Define multiple attributes that are exported only to OpenTelemetry spans. */
    public setOTLPAttributes(attributes: TraceLabels) {
        Object.assign(this.otlpAttributes, attributes);
    }

    /**
     * Return the entire state of the trace.
     */
    public state(): TraceState {
        return {
            ...globalState,
            ...this.traceState,
        };
    }

    /**
     * Return the HTTP headers to propagate the trace.
     */
    public headers() {
        return getHeadersForTrace({
            spanId: this.spanId,
            traceId: this.traceId,
            traceFlags: this.traceFlags,
            state: this.state(),
        });
    }

    /**
     * Return true if the trace is a root trace.
     */
    public isRootTrace() {
        return !this.parentSpanId;
    }

    /**
     * Stop exporting this trace and its descendants to OTLP.
     */
    public disableOTLPExport() {
        this.otlp = false;
    }

    /**
     * Start the trace
     */
    protected start() {
        this.startTime = time();
        this.startTimestamp = epochTime();
    }

    /**
     * Finish the trace and record it.
     */
    protected end(error: MaybeHTTPError | null = null) {
        if (this.startTime === null) {
            throw new Error('Ending a non-started trace');
        }

        this.endTime = time();
        this.endTimestamp = epochTime();
        const duration = this.endTime - this.startTime;

        // We don't log trace completion during unit tests as some assertions are expected to be failed (assertFailure)
        if (process.env.NODE_ENV !== 'test') {
            const labels = Object.keys(this.labels).length ? this.labels : undefined;

            /** Log a message with labels, protecting against undefined. */
            const logWithLabels = (fn: DebugLoggerFn, message: string) => {
                if (labels) {
                    fn.call(this.logger, message, labels);
                } else {
                    fn.call(this.logger, message);
                }
            };

            if (error) {
                const errorString =
                    process.env.NODE_ENV === 'development' ? error.stack : error.message;
                const message = `failed in ${duration.toFixed(2)}ms - ${errorString}`;

                // A 4xx is the caller asking for something it cannot have; nothing went wrong
                // on our side, so it is not a failure of the traced operation. `expose` is a
                // weaker signal - it only says the message is safe to show - and covers 5xx and
                // status-less errors too, so it cannot stand in for this.
                //
                // Read the status through `extractHTTPError` rather than off the error: one that
                // crossed a tRPC boundary carries it under `data.httpError`, and the client-side
                // link traces every RPC, so reading the property alone would call those 4xx
                // critical. Anything with no status to find is reported as a 500 and falls
                // through, which is what an unknown error should do here.
                const { statusCode } = extractHTTPError(error);
                if (
                    statusCode >= HTTPStatusCode.BadRequest &&
                    statusCode < HTTPStatusCode.InternalServerError
                ) {
                    logWithLabels(this.logger.warning, message);
                } else if (error.expose) {
                    logWithLabels(this.logger.error, message);
                } else {
                    logWithLabels(this.logger.critical, message);
                }
            } else {
                const fn = this.silent ? this.logger.debug : this.logger.info;
                const message = `succeeded in ${duration.toFixed(2)}ms`;
                if (this.logLabelsOnSuccess) {
                    logWithLabels(fn, message);
                } else {
                    fn.call(this.logger, message);
                }
            }
        }
    }

    /**
     * Utility to wrap some async code, the wrapped function is provided a logger, which is labelled
     * with the trace labels.
     */
    public async wrap<T>(fn: () => Promise<T>): Promise<T> {
        try {
            this.start();
            const result = await fn();
            this.end();
            return result;
        } catch (error) {
            assertError(error);
            this.end(error);
            throw error;
        }
    }

    /**
     * Utility to wrap some sync code.
     */
    public wrapSync<T>(fn: () => T): T {
        try {
            this.start();
            const result = fn();
            this.end();
            return result;
        } catch (error) {
            assertError(error);
            this.end(error);
            throw error;
        }
    }
}
